// The `eslint-disable-next-line no-unused-vars` that used to sit here is gone,
// not moved: this file is TypeScript now, eslint.config.js block D replaces the
// core rule with @typescript-eslint/no-unused-vars for .tsx, and that rule
// already understands JSX-only use. The directive became an "unused disable
// directive" warning of its own.
import type {KeyboardEvent} from 'react';
import Badge from '../shared/Badge.jsx';
import {pigBatchProcessingStatusLabel} from '../lib/processingStatusDisplay.js';

// Shared column template for the unified Pig Batches inspection grid. The hub
// header row (PigBatchesView) and every batch row (this component) consume the
// SAME template so the columns stay aligned, Podio-style.
export const PIG_BATCH_GRID_COLUMNS = 'minmax(140px, 1.6fr) 88px repeat(12, minmax(82px, 0.8fr))';

export const PIG_BATCH_GRID_HEADERS = Object.freeze([
  'Batch',
  'Status',
  'Started Head',
  'Current Head',
  'Total Feed',
  'Feed / Pig',
  'Gilts Started',
  'Gilts Current',
  'Gilts Total Feed',
  'Gilts Feed / Pig',
  'Boars Started',
  'Boars Current',
  'Boars Total Feed',
  'Boars Feed / Pig',
]);

// Every metric reaching a cell is already a number or null out of
// buildPigBatchGridMetrics, but Number() is applied anyway and the parameter is
// left wide because these three helpers are the module's own not-recorded
// guard: `unknown` keeps that guard honest rather than asserting it away.
function finiteNumber(value: unknown): number | null {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

const EMPTY_CELL = '—'; // em dash, app convention for not-recorded values

function formatHead(value: unknown): string {
  const n = finiteNumber(value);
  return n == null ? EMPTY_CELL : Math.round(n).toLocaleString();
}

function formatFeed(value: unknown): string {
  const n = finiteNumber(value);
  return n != null && n > 0 ? Math.round(n).toLocaleString() + ' lb' : EMPTY_CELL;
}

const ESTIMATED_HINT = 'Estimated from started-head split';

type MetricCellProps = {
  value: number | null | undefined;
  // R19 (a): the column header, shown above the value only in the narrow card
  // layout (pigBatchesGrid.css); hidden wherever the header row is visible.
  label?: string;
  // 'feed' appends the ' lb' unit and hides zero; 'head' rounds and keeps zero.
  kind?: 'head' | 'feed';
  estimated?: boolean;
};

function MetricCell({value, kind = 'head', estimated = false, label}: MetricCellProps) {
  const display = kind === 'feed' ? formatFeed(value) : formatHead(value);
  const empty = display === EMPTY_CELL;
  // CP0 WI-2a: feed/head metrics are raw numbers → black (no amber tint).
  const color = empty ? 'var(--ink-faint)' : 'var(--text-primary)';
  // '~' marks proportional started-head estimates (source records carried no
  // sex attribution); exact single-sex values render unmarked.
  const marked = estimated && !empty;
  return (
    <span
      title={marked ? ESTIMATED_HINT : undefined}
      aria-label={marked ? ESTIMATED_HINT + ': ' + display : undefined}
      style={{
        fontSize: 12,
        color,
        fontVariantNumeric: 'tabular-nums',
        textAlign: 'right',
        whiteSpace: 'nowrap',
      }}
    >
      {label && <span className="pig-batch-cell-label">{label}</span>}
      {empty ? display : <strong>{(marked ? '~' : '') + display}</strong>}
    </span>
  );
}

// Presentational nav-only hub row. The caller computes ledger/feed metrics
// because those need breeders + dailys; this component displays fixed columns
// and routes on click/keyboard activation.
// One sex slice of the grid metrics. Shape is derived from blankSexMetrics +
// finalizeSexMetrics in src/lib/pigBatchGridMetrics.js, not invented: `current`
// and `feedPerPig` really do start as null, and the two *Estimated flags are
// what drive the '~' marker above.
type PigBatchSexMetrics = {
  started?: number | null;
  current?: number | null;
  totalFeedLbs?: number | null;
  feedPerPig?: number | null;
  currentEstimated?: boolean;
  feedEstimated?: boolean;
};

// The metrics object buildPigBatchGridMetrics returns. Every field is optional
// here because the component's own `metrics || {}` fallback says the caller may
// pass nothing at all, and PigBatchesView reads from a memo map that can miss.
type PigBatchGridMetrics = {
  started?: number | null;
  current?: number | null;
  totalFeedLbs?: number | null;
  feedPerPig?: number | null;
  gilts?: PigBatchSexMetrics;
  boars?: PigBatchSexMetrics;
};

// Only the three fields this row renders. The rest of a pig batch group lives
// in the ppp-feeders-v1 blob with no declaration this repo owns, and the two
// status helpers take it as ProcessingStatusSource ({status?: unknown}) anyway.
type PigBatchGroup = {
  id: string;
  batchName?: string | null;
  status?: string | null;
};

type PigBatchHubTileProps = {
  group: PigBatchGroup;
  metrics?: PigBatchGridMetrics | null;
  // Destructured but never read in this file, and passed by no call site.
  // Retained rather than deleted because this wave converts types, not
  // behaviour; typed `unknown` because nothing here establishes what it means.
  statusColor?: unknown;
  onOpen?: () => void;
};

export default function PigBatchHubTile({group, metrics, statusColor, onOpen}: PigBatchHubTileProps) {
  const rowMetrics = metrics || {};
  const gilts = rowMetrics.gilts || {};
  const boars = rowMetrics.boars || {};
  const statusLabel = pigBatchProcessingStatusLabel(group, rowMetrics);

  function handleKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    if (typeof onOpen === 'function') onOpen();
  }

  return (
    <div
      data-pig-batch-tile={group.id}
      data-pig-batch-openable-row="1"
      onClick={onOpen}
      onKeyDown={handleKeyDown}
      className="hoverable-tile"
      role="button"
      tabIndex={0}
      aria-label={'Open pig batch ' + (group.batchName || group.id || '')}
      style={{
        display: 'grid',
        gridTemplateColumns: PIG_BATCH_GRID_COLUMNS,
        gap: 10,
        alignItems: 'center',
        padding: '10px 14px',
        // R02: a row rule inside the card is the hairline.
        borderBottom: '1px solid var(--hairline)',
        cursor: 'pointer',
        background: group.status === 'processed' ? 'var(--surface-2)' : 'var(--surface)',
      }}
    >
      {/* R19: no sticky column at any width. */}
      <span
        style={{
          fontWeight: 700,
          fontSize: 13,
          color: 'var(--ink)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {group.batchName}
      </span>

      <span style={{justifySelf: 'start'}}>
        <Badge status={statusLabel}>{statusLabel}</Badge>
      </span>

      {/* Each cell carries its own header for the narrow card layout, read
          from the same PIG_BATCH_GRID_HEADERS the header row maps. */}
      <MetricCell value={rowMetrics.started} label={PIG_BATCH_GRID_HEADERS[2]} />
      <MetricCell value={rowMetrics.current} label={PIG_BATCH_GRID_HEADERS[3]} />
      <MetricCell value={rowMetrics.totalFeedLbs} kind="feed" label={PIG_BATCH_GRID_HEADERS[4]} />
      <MetricCell value={rowMetrics.feedPerPig} kind="feed" label={PIG_BATCH_GRID_HEADERS[5]} />
      <MetricCell value={gilts.started} label={PIG_BATCH_GRID_HEADERS[6]} />
      <MetricCell value={gilts.current} estimated={gilts.currentEstimated} label={PIG_BATCH_GRID_HEADERS[7]} />
      <MetricCell
        value={gilts.totalFeedLbs}
        kind="feed"
        estimated={gilts.feedEstimated}
        label={PIG_BATCH_GRID_HEADERS[8]}
      />
      <MetricCell
        value={gilts.feedPerPig}
        kind="feed"
        estimated={gilts.feedEstimated}
        label={PIG_BATCH_GRID_HEADERS[9]}
      />
      <MetricCell value={boars.started} label={PIG_BATCH_GRID_HEADERS[10]} />
      <MetricCell value={boars.current} estimated={boars.currentEstimated} label={PIG_BATCH_GRID_HEADERS[11]} />
      <MetricCell
        value={boars.totalFeedLbs}
        kind="feed"
        estimated={boars.feedEstimated}
        label={PIG_BATCH_GRID_HEADERS[12]}
      />
      <MetricCell
        value={boars.feedPerPig}
        kind="feed"
        estimated={boars.feedEstimated}
        label={PIG_BATCH_GRID_HEADERS[13]}
      />
    </div>
  );
}
